import { StepType } from "@reactour/tour";
import eventBus from "@/lib/eventbus";
import { TOUR_EVENT } from "@/lib/tour-events";
import { TOUR_TARGET, tourSelector } from "@/lib/tour-targets";

const highlightedAreaStyle = {
  border: "2px solid #3B82F6",
  borderRadius: "4px",
  boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5), 0 0 15px rgba(0, 0, 0, 0.3)",
};

// Green dashed emphasis used for the "look around the stage" steps.
const stageHighlight = () => ({
  ...highlightedAreaStyle,
  border: "3px dashed #22C55E",
  boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.7), 0 0 20px rgba(34, 197, 94, 0.5)",
});

export const steps: StepType[] = [
  // ── Stage / "look around" ──────────────────────────────────────────────
  // These all target the live stream surface so the spotlight frames the real
  // video. (Previously these pointed at the empty 3D UI shell, which let the
  // dark mask paint over the GameLift video and made it look like it vanished.)
  {
    selector: tourSelector(TOUR_TARGET.stage),
    content: "Left Click and Drag your mouse across the screen to look around.",
    styles: { highlightedArea: stageHighlight },
    position: "center",
    padding: 20,
  },
  {
    selector: tourSelector(TOUR_TARGET.stage),
    content: "You can also look around using arrow keys.",
    styles: { highlightedArea: stageHighlight },
    position: "center",
    padding: 20,
  },
  {
    selector: tourSelector(TOUR_TARGET.stage),
    content: 'Press "1" to look at the Video Screen',
    position: "center",
    padding: 2,
  },
  {
    selector: tourSelector(TOUR_TARGET.stage),
    content: 'Press "2" to look at the Screenshare Screen',
    position: "center",
    padding: 2,
  },
  {
    selector: tourSelector(TOUR_TARGET.stage),
    content: 'Press "3" to look at the Document Screen',
    position: "center",
    padding: 2,
  },

  // ── Media controls ─────────────────────────────────────────────────────
  {
    selector: tourSelector(TOUR_TARGET.mic),
    content: "Click here to toggle the Microphone",
    stepInteraction: false,
  },
  {
    selector: tourSelector(TOUR_TARGET.camera),
    content: "Click here to toggle the Camera",
  },

  // ── Settings (opened via event bus, not a faked DOM click) ─────────────
  {
    selector: tourSelector(TOUR_TARGET.settingsButton),
    content: "Click here to open the Settings Menu",
    stepInteraction: true,
    // Open the dialog as the user leaves this step so the next step can
    // highlight the settings panel.
    actionAfter() {
      eventBus.publish(TOUR_EVENT.openSettings);
    },
  },
  {
    selector: tourSelector(TOUR_TARGET.settingsPanel),
    content: "Use these settings to select your Microphone and Camera",
    // Observe the settings dialog mounting so the tour can reposition.
    mutationObservables: [tourSelector(TOUR_TARGET.settingsPanel)],
    highlightedSelectors: [tourSelector(TOUR_TARGET.settingsPanel)],
    position: "top",
    padding: 2,
  },

  // ── Chat (opened via event bus) ────────────────────────────────────────
  {
    selector: tourSelector(TOUR_TARGET.chatButton),
    content: "Click here to open the chat",
    stepInteraction: true,
    actionAfter() {
      eventBus.publish(TOUR_EVENT.openChat);
    },
    position: "top",
    padding: 2,
  },
  {
    selector: tourSelector(TOUR_TARGET.chatPanel),
    content: "Here you can chat with other attendees",
    // Observe chat panel mounting so the tour can reposition to the chat content.
    mutationObservables: [tourSelector(TOUR_TARGET.chatPanel)],
    highlightedSelectors: [tourSelector(TOUR_TARGET.chatPanel)],
    position: "top",
    padding: 2,
  },

  // ── Misc surfaces ──────────────────────────────────────────────────────
  {
    selector: tourSelector(TOUR_TARGET.videoBackground),
    content: "Click here to choose a Video Background",
    position: "top",
    padding: 2,
  },
  {
    selector: tourSelector(TOUR_TARGET.help),
    content: "You can contact the Helpdesk for live support",
    position: "top",
    padding: 2,
    stepInteraction: false,
  },
  {
    selector: tourSelector(TOUR_TARGET.talkingHead),
    content:
      "You can see who is talking at any moment from here while navigating through the Zone",
    position: "top",
    padding: 2,
    stepInteraction: false,
  },
  {
    selector: tourSelector(TOUR_TARGET.resources),
    content:
      "You can see the documents shared by the Host in the Resource Center",
    position: "left",
    padding: 8,
    stepInteraction: false,
  },
  {
    selector: tourSelector(TOUR_TARGET.sponsors),
    content: "You can see the sponsors here.",
    position: "left",
    padding: 8,
    stepInteraction: false,
  },
];
